import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import PeriodButtons from "features/settings/Subscription/PeriodButtons";
import PlanCard from "features/settings/Subscription/PlanCard";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import {
  BASE_BILLING_SETTINGS_PATH,
  PAGE_SPINNER_MARGIN_FROM_TOP,
} from "common/constants";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import LeftOutlined from "@ant-design/icons/LeftOutlined";
import { Redirect, useHistory } from "react-router-dom";
import React, { useContext, useEffect } from "react";
import useMeasure from "utils/hooks/useMeasure.ts";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { typography } from "@shared/common/Themes/typography";
import Flex from "common/Boxes/Flex";
import useChoosePlan from "features/settings/Subscription/useChoosePlan.ts";
import { LanguageNameSpaces, SubscriptionPlanType } from "@shared/types";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import NewLookChoosePlan from "features/settings/Subscription/NewLook/NewLookChoosePlan.tsx";

const ChoosePlan = () => {
  const { isNewLook } = useTheme();
  return isNewLook ? <NewLookChoosePlan /> : <ClassicChoosePlan />;
};

const ClassicChoosePlan = () => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { isUpgrade, analyticsInvoiceType } = useContext(
    BuySubscriptionContext
  )!;
  const {
    isFetching,
    filteredPlans,
    getIsAllowChoosePlan,
    promoCodeDiscount,
    title,
  } = useChoosePlan();
  const history = useHistory();
  const { analytics } = useContext(AnalyticsContext);
  const { user } = useUser();
  const [measureRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();

  const checkRedirects = () => {
    const allowChoosePlan = getIsAllowChoosePlan();
    if (!allowChoosePlan) {
      return BASE_BILLING_SETTINGS_PATH;
    }
  };

  const redirect = checkRedirects();

  useEffect(() => {
    if (!redirect) {
      analytics?.purchasePlanChoosePlanPageView({
        subscriptionInvoiceType: analyticsInvoiceType,
      });
    }
  }, [analytics, redirect]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  const regularPlans =
    filteredPlans.filter(
      (plan) => !isWhitelabelSubscriptionType(plan.subscription_plan_type)
    ) || [];
  const enterprisePlans =
    filteredPlans?.filter((plan) =>
      isWhitelabelSubscriptionType(plan.subscription_plan_type)
    ) || [];
  const numRegular = regularPlans.length;
  const currentPlanType = user?.subscription_plan_type;

  const { regularCols, enterpriseCols, regularGridMaxWidth } =
    calculatePricingCols(containerWidth, numRegular);

  const getRowHasCurrentPlan = (
    plans: typeof regularPlans,
    numCols: number
  ) => {
    const currentIndex = plans.findIndex(
      (p) => p.subscription_plan_type === currentPlanType
    );
    if (currentIndex === -1) return plans.map(() => false);
    const currentRow = Math.floor(currentIndex / numCols);
    return plans.map((_, i) => Math.floor(i / numCols) === currentRow);
  };

  const regularRowHasCurrentPlan = getRowHasCurrentPlan(
    regularPlans,
    regularCols
  );
  const enterpriseRowHasCurrentPlan = getRowHasCurrentPlan(
    enterprisePlans,
    enterpriseCols
  );

  const getAvailableAllPlansText = () => {
    return (
      <AllPlansFeaturesWrapper>
        <Text style={{ fontWeight: 600 }}>
          {t("choose_plan.available_on_all_plans")}
        </Text>
        {AVAILABLE_ALL_PLAN_FEATURES.map((translationKey, index) => {
          return (
            <Flex key={index}>
              <CheckOutlined
                style={{ marginRight: 8, color: theme.colors.primaryText }}
              />
              <Text>{t(translationKey)}</Text>
            </Flex>
          );
        })}
      </AllPlansFeaturesWrapper>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={isFetching}
      spinnerSize="large"
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      hideContentWhileLoading={isFetching}
    >
      <Wrapper>
        <TopContainer>
          <Title
            $size={typography.fontSizes.XXXL}
            $weight={typography.fontWeights.SEMI_BOLD}
            style={{ position: "relative", paddingLeft: 50 }}
          >
            <BackButton
              onClick={() => history.push(BASE_BILLING_SETTINGS_PATH)}
              style={{ padding: 8 }}
            />
            {title}
          </Title>
          {!isUpgrade && (
            <PeriodButtons promoCodeDiscount={promoCodeDiscount} />
          )}
          {getAvailableAllPlansText()}
        </TopContainer>

        <PlansContainer>
          <GridsWrapper ref={measureRef}>
            {regularPlans.length > 0 && (
              <RegularGrid $cols={regularCols}>
                {regularPlans.map((plan, index) => (
                  <PlanCard
                    key={index}
                    plan={plan}
                    isUpgrade={isUpgrade}
                    promoCodeDiscount={promoCodeDiscount}
                    rowHasCurrentPlan={regularRowHasCurrentPlan[index]}
                  />
                ))}
              </RegularGrid>
            )}

            {enterprisePlans.length > 0 && (
              <EnterpriseGrid
                $cols={enterpriseCols}
                $maxWidth={regularGridMaxWidth}
              >
                {enterprisePlans.map((plan, index) => {
                  const descriptionKey =
                    ENTERPRISE_PLAN_DESCRIPTION_KEYS[
                      plan.subscription_plan_type!
                    ];
                  return (
                    <PlanCard
                      key={index}
                      plan={plan}
                      isUpgrade={isUpgrade}
                      promoCodeDiscount={promoCodeDiscount}
                      description={descriptionKey ? t(descriptionKey) : ""}
                      isEnterprise={true}
                      rowHasCurrentPlan={enterpriseRowHasCurrentPlan[index]}
                    />
                  );
                })}
              </EnterpriseGrid>
            )}
          </GridsWrapper>
        </PlansContainer>
      </Wrapper>
    </LoadingSpinnerOverlay>
  );
};

export default ChoosePlan;

const AVAILABLE_ALL_PLAN_FEATURES: TranslationKeys<LanguageNameSpaces.BILLING>[] =
  [
    "choose_plan.mobile_web_apps",
    "choose_plan.support_24_7",
    "choose_plan.automated_trading",
    "choose_plan.advanced_trading_features",
  ];

const GAP = 16;
const MAX_PLAN_WIDTH = 350;
const MIN_PLAN_WIDTH = 280;

const ENTERPRISE_PLAN_DESCRIPTION_KEYS: Partial<
  Record<SubscriptionPlanType, TranslationKeys<LanguageNameSpaces.BILLING>>
> = {
  [SubscriptionPlanType.ASSET_MANAGER]:
    "enterprise_plan_descriptions.asset_manager",
  [SubscriptionPlanType.COMMUNITY_ADMIN]:
    "enterprise_plan_descriptions.community_admin",
};

export function calculatePricingCols(
  containerWidth: number,
  planCount: number
): {
  regularCols: number;
  enterpriseCols: number;
  regularGridMaxWidth: number;
} {
  let cols = Math.floor((containerWidth + GAP) / (MIN_PLAN_WIDTH + GAP));
  cols = Math.min(cols, planCount);

  if (planCount === 4 && cols === 3) cols = 2;
  if (planCount === 3 && cols === 2) cols = 1;

  const regularGridMaxWidth = cols * MAX_PLAN_WIDTH + (cols - 1) * GAP;

  return {
    regularCols: cols,
    enterpriseCols: cols >= 2 ? 2 : 1,
    regularGridMaxWidth,
  };
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
  width: 100%;
  background: ${(p) => p.theme.colors.bgColor};
  margin: 0 auto;
`;
const TopContainer = styled.div`
  padding: 25px 20px 8px 20px;
  text-align: center;
  width: fit-content;

  @media (max-width: 630px) {
    padding: 25px 12px 8px 12px;

    h1,
    h2 {
      font-size: 28px;
      padding-left: 15px;
    }
  }
`;
const PlansContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 20px 20px 40px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  overflow: hidden;

  @media (max-width: 630px) {
    padding: 16px 12px 30px 12px;
  }
`;
const GridsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${GAP}px;
  width: 100%;
  align-items: center;
`;
const RegularGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${(p) => p.$cols},
    minmax(${MIN_PLAN_WIDTH}px, ${MAX_PLAN_WIDTH}px)
  );
  gap: ${GAP}px;
`;
const EnterpriseGrid = styled.div<{ $cols: number; $maxWidth: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, 1fr);
  max-width: ${(p) => p.$maxWidth}px;
  gap: ${GAP}px;
`;
const BackButton = styled(LeftOutlined)`
  font-size: 24px;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(0, -50%);
`;
const AllPlansFeaturesWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 12px;
  row-gap: 2px;
  max-width: 470px;
  margin: 20px auto 0 auto;
  @media (max-width: 460px) {
    width: 290px;
  }
`;
